import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { donationService } from '@services/donation.service';
import { useCampaignStore } from '@stores/campaign.store';
import { useAnalytics } from '@hooks/useAnalytics';
import type { DonationTier } from '@/types';

export function Donate() {
  const { t } = useTranslation();
  const { trackDonate, trackButtonClick, trackFormSubmission } = useAnalytics();
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { addNotification } = useCampaignStore();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const donationTiers: DonationTier[] = [
    { id: '1', amount: 25, label: 'Supporter', description: 'Join the movement' },
    { id: '2', amount: 50, label: 'Advocate', description: 'Make your voice heard', isPopular: true },
    { id: '3', amount: 100, label: 'Champion', description: 'Lead the change' },
    { id: '4', amount: 250, label: 'Patriot', description: 'Secure our future' },
  ];

  const handleDonate = async () => {
    const amount = selectedAmount || Number(customAmount);
    if (!amount || amount < 1) {
      addNotification({
        type: 'error',
        message: t('donate.error') || 'Please select or enter a valid amount.',
      });
      trackFormSubmission('donation', false);
      return;
    }

    setIsLoading(true);
    try {
      await donationService.createDonation({
        amount,
        donorInfo: {
          firstName: '',
          lastName: '',
          email: '',
          municipality: '',
        },
        paymentMethod: 'credit_card',
        isRecurring: false,
      });

      // Track successful donation
      trackDonate(amount, 'USD');
      trackFormSubmission('donation', true);

      addNotification({
        type: 'success',
        message: t('donate.success') || 'Thank you for your support!',
      });
    } catch (error) {
      trackFormSubmission('donation', false);
      addNotification({
        type: 'error',
        message: t('donate.error') || 'Payment processing failed.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="pt-24 pb-16 min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-heading font-black text-campaign-black mb-4">
            {t('donate.title')}
          </h1>
          <p className="text-xl text-gray-600">
            {t('donate.subtitle')}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="bg-white rounded-2xl shadow-xl p-8"
        >
          {/* Donation Tiers */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {donationTiers.map((tier) => (
              <button
                key={tier.id}
                onClick={() => {
                  setSelectedAmount(tier.amount);
                  setCustomAmount('');
                  trackButtonClick(`tier_${tier.amount}`, 'donate_page');
                }}
                className={`relative p-6 rounded-xl border-2 transition-all ${
                  selectedAmount === tier.amount
                    ? 'border-sky-600 bg-sky-50'
                    : 'border-gray-200 hover:border-sky-400'
                }`}
              >
                {tier.isPopular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-sky-200 text-sky-800 text-xs font-bold px-3 py-1 rounded-full">
                    {t('donate.popular')}
                  </span>
                )}
                <div className="text-3xl font-heading font-black text-campaign-black mb-1">
                  ${tier.amount}
                </div>
                <div className="text-sm font-semibold text-sky-600 mb-1">{tier.label}</div>
                <div className="text-xs text-gray-500">{tier.description}</div>
              </button>
            ))}
          </div>

          {/* Custom Amount */}
          <div className="mb-8">
            <label className="block text-sm font-semibold mb-2 text-campaign-black">
              {t('donate.customAmount')}
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
              <Input
                type="number"
                placeholder="Enter amount"
                value={customAmount}
                onChange={(e) => {
                  setCustomAmount(e.target.value);
                  setSelectedAmount(null);
                }}
                className="pl-8 text-lg"
              />
            </div>
          </div>

          {/* Donate Button */}
          <Button
            onClick={handleDonate}
            isLoading={isLoading}
            className="w-full text-lg py-4"
          >
            {t('donate.contribute')} {selectedAmount ? `$${selectedAmount}` : customAmount ? `$${customAmount}` : ''}
          </Button>

          <p className="text-center text-sm text-gray-500 mt-6">
            {t('donate.disclaimer')}
          </p>
        </motion.div>
      </div>
    </div>
  );
}